import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, Compass, ChevronDown } from "lucide-react";
import { NOURISH_MENU } from "@/data/diet";

const links = [
  { href: "/#destinations", label: "Destinations", tid: "nav-link-destinations" },
  { href: "/#collections", label: "Collections", tid: "nav-link-collections" },
  { href: "/calendar", label: "Calendar", tid: "nav-link-calendar" },
  { href: "/journal", label: "Journal", tid: "nav-link-journal" },
  { href: "/train", label: "Train", tid: "nav-link-train" },
  { href: "/#coffee", label: "Coffee", tid: "nav-link-coffee", accent: true },
  { href: "/coaching", label: "Coaching", tid: "nav-link-coaching" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const darkHero = pathname === "/" || pathname.startsWith("/destinations") || pathname === "/retreats";
  const light = darkHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#F7F5F0]/80 border-b border-[#E5E2D9]/60" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-5">
        <a href="/" data-testid="nav-logo" className="flex items-baseline gap-2">
          <span className={`font-serif-display text-2xl md:text-3xl font-medium tracking-tight ${light ? "text-[#F7F5F0]" : "text-[#2D372B]"}`}>
            TheJayProject
          </span>
          <span className="hidden sm:block h-1.5 w-1.5 rounded-full bg-[#AD6A48]" />
        </a>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.tid}
              className={`text-sm transition-colors inline-flex items-center gap-1.5 ${
                l.accent
                  ? light ? "text-[#E8C9B8] font-medium hover:text-white" : "text-[#AD6A48] font-medium hover:text-[#8A5236]"
                  : light ? "text-[#C9CFC0] hover:text-white" : "text-[#5C665A] hover:text-[#2D372B]"
              }`}
            >
              {l.accent && <span className="h-1.5 w-1.5 rounded-full bg-[#AD6A48]" />}
              {l.label}
            </a>
          ))}
          <div className="relative group" data-testid="nav-nourish">
            <button
              type="button"
              className={`text-sm transition-colors inline-flex items-center gap-1 ${
                light ? "text-[#C9CFC0] hover:text-white" : "text-[#5C665A] hover:text-[#2D372B]"
              }`}
              data-testid="nav-link-nourish"
            >
              Nourish <ChevronDown size={13} className="transition-transform group-hover:rotate-180" />
            </button>
            <div className="absolute right-0 top-full pt-3 opacity-0 invisible translate-y-1 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-200">
              <div className="w-64 rounded-2xl bg-[#2D372B] shadow-2xl border border-white/10 p-2" data-testid="nourish-dropdown">
                {NOURISH_MENU.map((m, i) => (
                  <a
                    key={m.href}
                    href={m.href}
                    data-testid={`nourish-item-${m.href.split("/").pop()}`}
                    className={`block rounded-xl px-4 py-2.5 text-sm transition-colors hover:bg-white/10 ${
                      m.strong ? "text-[#E8C9B8] font-medium" : "text-[#C9CFC0]"
                    } ${i === 1 ? "border-b border-white/10 mb-1 pb-3.5" : ""}`}
                  >
                    {m.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="/login"
            data-testid="nav-member-login"
            className={`inline-flex items-center rounded-full px-5 py-2.5 border text-sm font-medium transition-colors ${
              light ? "border-[#F7F5F0]/40 text-[#F7F5F0] hover:bg-[#F7F5F0]/10" : "border-[#2D372B]/20 text-[#2D372B] hover:border-[#2D372B]"
            }`}
          >
            Member Login
          </a>
          <a
            href="/journey"
            data-testid="nav-cta"
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 bg-[#C57B57] text-white text-sm font-medium hover:bg-[#AD6A48] transition-colors"
          >
            <Compass size={14} /> Plan My Journey
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-3">
          <a
            href="/login"
            data-testid="nav-mobile-login-btn"
            className={`inline-flex items-center rounded-full px-4 py-2 border text-xs font-medium transition-colors ${
              light ? "border-[#F7F5F0]/40 text-[#F7F5F0]" : "border-[#2D372B]/25 text-[#2D372B]"
            }`}
          >
            Login
          </a>
          <button
            onClick={() => setOpen((o) => !o)}
            className={light ? "text-[#F7F5F0]" : "text-[#2D372B]"}
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E5E2D9] bg-[#F7F5F0]/95 backdrop-blur-xl max-h-[calc(100vh-72px)] overflow-y-auto">
          <div className="px-6 py-6 flex flex-col gap-5">
            <a
              href="/login"
              data-testid="nav-mobile-member-login"
              onClick={() => setOpen(false)}
              className="rounded-full px-5 py-3 border-2 border-[#2D372B] text-[#2D372B] text-center font-medium"
            >
              Member Login
            </a>
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                onClick={() => setOpen(false)}
                className={`text-lg ${l.accent ? "text-[#AD6A48] font-medium" : "text-[#2D372B]"}`}
              >
                {l.label}
              </a>
            ))}
            <div>
              <div className="text-lg text-[#2D372B] font-medium mb-2" data-testid="nav-mobile-nourish">Nourish</div>
              <div className="pl-3 flex flex-col gap-2.5 border-l-2 border-[#E5E2D9]">
                {NOURISH_MENU.map((m) => (
                  <a key={m.href} href={m.href} onClick={() => setOpen(false)} className={`text-base ${m.strong ? "text-[#AD6A48] font-medium" : "text-[#5C665A]"}`}>
                    {m.label}
                  </a>
                ))}
              </div>
            </div>
            <a
              href="/journey"
              onClick={() => setOpen(false)}
              className="rounded-full px-5 py-3 bg-[#C57B57] text-white text-center font-medium"
            >
              Plan My Journey
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
